import './model-beschikbaarheid.scss';

const WEEKDAGEN = [ 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo' ];

class ModelBeschikbaarheid {
	constructor( el ) {
		this.el          = el;
		this.postId      = el.dataset.postId;
		this.nonce       = el.dataset.nonce;
		this.ajaxUrl     = el.dataset.ajaxUrl;
		this.today       = el.dataset.today;
		this.slotLabels  = JSON.parse( el.dataset.slotLabels  || '{}' );
		this.maanden     = JSON.parse( el.dataset.maanden     || '[]' );

		this.calendarEl   = el.querySelector( '.sb-beschikbaarheid__calendar' );
		this.slotPickerEl = el.querySelector( '.sb-beschikbaarheid__slot-picker' );

		const todayDate = new Date( this.today + 'T00:00:00' );
		this.year  = todayDate.getFullYear();
		this.month = todayDate.getMonth() + 1;

		// Map: YYYY-MM → { availability, hasPrevMonth, hasNextMonth }
		this.cache = new Map();
		// Map: dateStr → slots[] for current month (avoids HTML attribute escaping)
		this.daySlots = new Map();
		// Map: dateStr → [{ slot, label, disabled }]
		this.slotStatesByDate = new Map();

		this.selectedDate = null;

		this.fetchAndRender( this.year, this.month );
	}

	async fetchAndRender( year, month ) {
		this.showLoading();

		const key = `${ year }-${ String( month ).padStart( 2, '0' ) }`;

		let data;
		if ( this.cache.has( key ) ) {
			data = this.cache.get( key );
			console.log( 'Using cached data for', key, data );
		} else {
			const body = new FormData();
			body.append( 'action',   'sb_get_model_month_availability' );
			body.append( 'nonce',    this.nonce );
			body.append( 'post_id',  this.postId );
			body.append( 'year',     year );
			body.append( 'month',    month );

			try {
				const res  = await fetch( this.ajaxUrl, { method: 'POST', body } );
				const json = await res.json();
				if ( ! json.success ) {
					this.showError();
					return;
				}
				data = json.data;
				this.cache.set( key, data );
			} catch {
				this.showError();
				return;
			}

			console.log( 'Fetched data for', key, data );
		}

		this.year  = year;
		this.month = month;
		this.selectedDate = null;
		this.renderCalendar( data );
		this.slotPickerEl.hidden    = true;
		this.slotPickerEl.innerHTML = '';
	}

	renderCalendar( data ) {
		const { availability, slotsByDate = {}, hasPrevMonth, hasNextMonth, model, blockedDates = [] } = data;
		const monthLabel = this.maanden[ this.month - 1 ] || '';
		const blocked    = new Set( blockedDates );

		// Map date → slots for event handling without data-attribute encoding issues.
		this.daySlots = new Map( Object.entries( availability ) );
		this.slotStatesByDate = new Map( Object.entries( slotsByDate ) );

		const firstDay   = new Date( this.year, this.month - 1, 1 );
		const daysInMonth = new Date( this.year, this.month, 0 ).getDate();

		// ISO Monday-first offset (getDay: 0=Sun → becomes 7, then offset = dow - 1).
		let firstDow = firstDay.getDay();
		firstDow = firstDow === 0 ? 7 : firstDow;
		const offset = firstDow - 1;

		let html = `
			<div class="sb-beschikbaarheid__nav">
				<button
					type="button"
					class="sb-beschikbaarheid__nav-btn js-prevMonth"
					${ ! hasPrevMonth ? 'disabled aria-disabled="true"' : '' }
					aria-label="Vorige maand"
				>&#8592;</button>
				<span class="sb-beschikbaarheid__nav-title">${ monthLabel } ${ this.year }</span>
				<button
					type="button"
					class="sb-beschikbaarheid__nav-btn js-nextMonth"
					${ ! hasNextMonth ? 'disabled aria-disabled="true"' : '' }
					aria-label="Volgende maand"
				>&#8594;</button>
			</div>
			<div class="sb-beschikbaarheid__grid" role="grid" aria-label="${ monthLabel } ${ this.year }">
				${ WEEKDAGEN.map( d => `<div class="sb-beschikbaarheid__weekday" role="columnheader">${ d }</div>` ).join( '' ) }
		`;

		for ( let i = 0; i < offset; i++ ) {
			html += '<div class="sb-beschikbaarheid__day sb-beschikbaarheid__day--empty" role="gridcell" aria-hidden="true"></div>';
		}

		for ( let d = 1; d <= daysInMonth; d++ ) {
			const dateStr    = `${ this.year }-${ String( this.month ).padStart( 2, '0' ) }-${ String( d ).padStart( 2, '0' ) }`;
			const isBlocked  = blocked.has( dateStr );
			const isToday    = dateStr === this.today;
			const isPast     = dateStr < this.today;
			const isAvail    = ! isBlocked && ! isPast && this.daySlots.has( dateStr );
			const isSelected = dateStr === this.selectedDate;

			let cls = 'sb-beschikbaarheid__day';
			if ( isBlocked )  cls += ' sb-beschikbaarheid__day--blocked';
			else if ( isAvail ) cls += ' sb-beschikbaarheid__day--available';
			else              cls += ' sb-beschikbaarheid__day--disabled';
			if ( isToday )    cls += ' sb-beschikbaarheid__day--today';
			if ( isSelected ) cls += ' sb-beschikbaarheid__day--selected';

			if ( isAvail ) {
				html += `<div class="${ cls }" data-date="${ dateStr }" tabindex="0" role="button" aria-label="${ d } ${ monthLabel }"${ isSelected ? ' aria-pressed="true"' : '' }>${ d }</div>`;
			} else {
				html += `<div class="${ cls }" role="gridcell" aria-label="${ d } ${ monthLabel }" aria-disabled="true"${ isBlocked ? ' title="Gesloten dag"' : '' }>${ d }</div>`;
			}
		}

		html += '</div>';

		this.calendarEl.innerHTML = html;

		// Navigation
		const prevBtn = this.calendarEl.querySelector( '.js-prevMonth' );
		const nextBtn = this.calendarEl.querySelector( '.js-nextMonth' );
		if ( prevBtn ) prevBtn.addEventListener( 'click', () => this.goToPrevMonth() );
		if ( nextBtn ) nextBtn.addEventListener( 'click', () => this.goToNextMonth() );

		// Day click / keyboard
		this.calendarEl.querySelectorAll( '.sb-beschikbaarheid__day--available' ).forEach( cell => {
			cell.addEventListener( 'click', () => this.onDayClick( cell.dataset.date, data ) );
			cell.addEventListener( 'keydown', e => {
				if ( e.key === 'Enter' || e.key === ' ' ) {
					e.preventDefault();
					this.onDayClick( cell.dataset.date );
				}
			} );
		} );
	}

	onDayClick( date, data ) {
		this.selectedDate = date;

		this.calendarEl.querySelectorAll( '.sb-beschikbaarheid__day' ).forEach( c => {
			const sel = c.dataset.date === date;
			c.classList.toggle( 'sb-beschikbaarheid__day--selected', sel );
			if ( c.hasAttribute( 'aria-pressed' ) ) {
				c.setAttribute( 'aria-pressed', sel ? 'true' : 'false' );
			}
		} );


		const slotStates = this.slotStatesByDate.get( date ) || [];
		this.renderSlotPicker( date, slotStates, data );
	}

	renderSlotPicker( date, slotStates, data ) {
		const [ year, month, day ] = date.split( '-' );
		const model = data.model
		const monthLabel = this.maanden[ parseInt( month, 10 ) - 1 ] || '';
		const dayNum     = parseInt( day, 10 );

		let html = `
			<p class="sb-beschikbaarheid__slot-title">
				<strong>${ dayNum } ${ monthLabel } ${ year }</strong>
			</p>

			<p class="sb-beschikbaarheid__slot-subtitle">
				${ model }
			</p>
			<div class="sb-beschikbaarheid__slot-list">
		`;

		slotStates.forEach( slotState => {
			const slot = slotState?.slot || '';
			if ( ! slot ) {
				return;
			}
			const timeLabel = slotState?.label || this.slotLabels[ slot ] || slot;
			const disabled = slotState?.disabled === true;
			html += `
				<span type="button" class="sb-beschikbaarheid__slot-btn js-slotBtn" data-slot="${ slot }" data-date="${ date }">
					<span class="sb-beschikbaarheid__slot-code">${ slot }</span>
					<span class="sb-beschikbaarheid__slot-availability">
						${ disabled ? '<svg class="sb-beschikbaarheid__slot-icon icon-red" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M183.1 137.4C170.6 124.9 150.3 124.9 137.8 137.4C125.3 149.9 125.3 170.2 137.8 182.7L275.2 320L137.9 457.4C125.4 469.9 125.4 490.2 137.9 502.7C150.4 515.2 170.7 515.2 183.2 502.7L320.5 365.3L457.9 502.6C470.4 515.1 490.7 515.1 503.2 502.6C515.7 490.1 515.7 469.8 503.2 457.3L365.8 320L503.1 182.6C515.6 170.1 515.6 149.8 503.1 137.3C490.6 124.8 470.3 124.8 457.8 137.3L320.5 274.7L183.1 137.4z"/></svg>' : '<svg class="sb-beschikbaarheid__slot-icon icon-green" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M530.8 134.1C545.1 144.5 548.3 164.5 537.9 178.8L281.9 530.8C276.4 538.4 267.9 543.1 258.5 543.9C249.1 544.7 240 541.2 233.4 534.6L105.4 406.6C92.9 394.1 92.9 373.8 105.4 361.3C117.9 348.8 138.2 348.8 150.7 361.3L252.2 462.8L486.2 141.1C496.6 126.8 516.6 123.6 530.9 134z"/></svg>' }
					</span>
				</span>
			`;
		} );

		html += '</div>';

		this.slotPickerEl.innerHTML = html;
		this.slotPickerEl.hidden    = false;

		this.slotPickerEl.querySelectorAll( '.js-slotBtn' ).forEach( btn => {
			if ( btn.disabled ) {
				return;
			}
			btn.addEventListener( 'click', () => this.onSlotClick( btn.dataset.date, btn.dataset.slot, btn ) );
		} );

		// Scroll picker into view on mobile so the user sees the slot buttons.
		this.slotPickerEl.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
	}

	onSlotClick( date, slot, btn ) {
		this.slotPickerEl.querySelectorAll( '.js-slotBtn' ).forEach( b => {
			b.classList.toggle( 'sb-beschikbaarheid__slot-btn--selected', b === btn );
			b.setAttribute( 'aria-pressed', b === btn ? 'true' : 'false' );
		} );

		// Placeholder — to be replaced by booking integration.
		// eslint-disable-next-line no-console
		console.log( { date, slot } );
	}

	goToPrevMonth() {
		let m = this.month - 1;
		let y = this.year;
		if ( m < 1 ) { m = 12; y--; }
		this.fetchAndRender( y, m );
	}

	goToNextMonth() {
		let m = this.month + 1;
		let y = this.year;
		if ( m > 12 ) { m = 1; y++; }
		this.fetchAndRender( y, m );
	}

	showLoading() {
		this.calendarEl.innerHTML = '<div class="sb-beschikbaarheid__state">Laden…</div>';
	}

	showError() {
		this.calendarEl.innerHTML = '<div class="sb-beschikbaarheid__state sb-beschikbaarheid__state--error">Er is een fout opgetreden.</div>';
	}
}

document.querySelectorAll( '.js-modelBeschikbaarheid' ).forEach( el => new ModelBeschikbaarheid( el ) );
